import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Minimize2, Maximize2, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MessageBubble from './MessageBubble';
import SystemMessage from './SystemMessage';
import MessageInput from './MessageInput';
import StakeholdersList from './StakeholdersList';
import chatAPI from '../api/Chat';
import { groupMessagesByDate, getDateHeaderText, canSendMessage, getRoleColor } from '../../utils/chatUtils';
import Swal from 'sweetalert2';

const ChatPopup = ({ eventId, eventStatus, onClose, initialPosition = { bottom: 20, right: 20 } }) => {
  const { t } = useTranslation(['events']);
  const messagesEndRef = useRef(null);
  const [chatData, setChatData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStakeholders, setShowStakeholders] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentUserId] = useState(sessionStorage.getItem('user_id'));
  const [currentUserRole] = useState(sessionStorage.getItem('user_group'));
  const [isPolling, setIsPolling] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Polling interval reference
  const pollingIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatData = useCallback(async (showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) {
        setLoading(true);
      }
      
      const data = await chatAPI.getChatMessages(eventId);
      setChatData(data);
      setMessages(data.messages || []);
      setUnreadCount(data.unread_count || 0);
      setError(null);
      
      // Mark messages as read when chat is opened
      if (data.unread_count > 0) {
        try {
          await chatAPI.markAsRead(eventId);
          setUnreadCount(0);
        } catch (markReadError) {
          console.error('Failed to mark messages as read:', markReadError);
        }
      }
    } catch (err) {
      console.error('Error fetching chat data:', err);
      setError('Failed to load chat messages');
      if (err.response?.status === 403) {
        Swal.fire({
          icon: 'error',
          title: t('chat.access_denied'),
          text: t('chat.no_permission'),
        }).then(() => {
          onClose();
        });
      }
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  }, [eventId, onClose, t]);

  const pollForUpdates = useCallback(async () => {
    if (isPolling) return;
    
    setIsPolling(true);
    try {
      await fetchChatData(false);
    } catch (error) {
      console.error('Polling error:', error);
    } finally {
      setIsPolling(false);
    }
  }, [fetchChatData, isPolling]);

  const handleSendMessage = async (message) => {
    try {
      const response = await chatAPI.sendMessage(eventId, message);
      
      // Optimistically add the message to the UI
      const newMessage = {
        id: response.id || Date.now(),
        message: message,
        sender_name: sessionStorage.getItem('username') || 'You',
        sender_display_name: `${sessionStorage.getItem('username') || 'You'} (${currentUserRole})`,
        sender_role: currentUserRole,
        message_type: 'user_message',
        created_at: new Date().toISOString(),
        is_read: false,
        is_deleted: false,
        can_delete: true
      };
      
      setMessages(prev => [...prev, newMessage]);
      
      // Fetch fresh data to get the actual message from server
      setTimeout(() => {
        fetchChatData(false);
      }, 500);
      
    } catch (error) {
      console.error('Failed to send message:', error);
      Swal.fire({
        icon: 'error',
        title: t('chat.failed_to_send'),
        text: error.response?.data?.detail || t('chat.try_again')
      });
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await chatAPI.deleteMessage(eventId, messageId);
      
      // Update the message in the UI
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, is_deleted: true } 
            : msg
        )
      );
      
      Swal.fire({
        icon: 'success',
        title: t('chat.message_deleted'),
        timer: 2000,
        showConfirmButton: false
      });
      
    } catch (error) {
      console.error('Failed to delete message:', error);
      Swal.fire({
        icon: 'error',
        title: t('chat.failed_to_delete'),
        text: error.response?.data?.detail || t('chat.try_again')
      });
    }
  };

  // Initial load
  useEffect(() => {
    fetchChatData();
  }, [fetchChatData]);

  // Set up polling for real-time updates
  useEffect(() => {
    const startPolling = () => {
      pollingIntervalRef.current = setInterval(pollForUpdates, 5000);
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    if (!isMinimized) {
      startPolling();
    } else {
      stopPolling();
    }

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.hidden || isMinimized) {
        stopPolling();
      } else {
        startPolling();
        pollForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pollForUpdates, isMinimized]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (!isMinimized) {
      scrollToBottom();
    }
  }, [messages, isMinimized]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirm':
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'revoke':
      case 'revoked':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirm':
      case 'confirmed':
        return t('statuses.confirmed');
      case 'revoke':
      case 'revoked':
        return t('statuses.revoked');
      case 'pending':
        return t('statuses.pending');
      default:
        return status || t('statuses.pending');
    }
  };

  const canSend = canSendMessage(eventStatus);
  const groupedMessages = groupMessagesByDate(messages);

  // Chat popup styles based on state
  const getPopupStyles = () => {
    if (isMaximized) {
      return {
        position: 'fixed',
        top: '10px',
        left: '10px',
        right: '10px',
        bottom: '10px',
        width: 'auto',
        height: 'auto',
        zIndex: 1000
      };
    }
    
    if (isMinimized) {
      return {
        position: 'fixed',
        bottom: `${initialPosition.bottom}px`,
        right: `${initialPosition.right}px`,
        width: '300px',
        height: '60px',
        zIndex: 1000
      };
    }
    
    return {
      position: 'fixed',
      bottom: `${initialPosition.bottom}px`,
      right: `${initialPosition.right}px`,
      width: '400px',
      height: '600px',
      zIndex: 1000
    };
  };

  if (loading) {
    return (
      <div 
        style={getPopupStyles()}
        className="bg-white rounded-lg shadow-2xl border border-gray-200 flex items-center justify-center"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">{t('chat.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        style={getPopupStyles()}
        className="bg-white rounded-lg shadow-2xl border border-gray-200 flex items-center justify-center"
      >
        <div className="text-center p-4">
          <div className="text-red-600 mb-2">⚠️</div>
          <p className="text-sm text-red-600 mb-2">{error}</p>
          <button
            onClick={() => fetchChatData()}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            {t('chat.try_again')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={getPopupStyles()}
      className="bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="bg-blue-500 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2 flex-1">
          <MessageCircle size={18} />
          <div className="min-w-0 flex-1">
            <h3 className="font-medium truncate text-sm">
              {chatData?.event?.name || t('chat.title')}
            </h3>
            <div className="flex items-center space-x-2 text-xs">
              <span 
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(eventStatus)}`}
                style={{ fontSize: '10px' }}
              >
                {getStatusText(eventStatus)}
              </span>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-1 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-1">
          <StakeholdersList 
            stakeholders={chatData?.stakeholders || []}
            isOpen={showStakeholders}
            onToggle={() => setShowStakeholders(!showStakeholders)}
          />
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-blue-600 rounded"
            title={isMinimized ? "Expand" : "Minimize"}
          >
            <Minimize2 size={16} />
          </button>
          <button
            onClick={() => setIsMaximized(!isMaximized)}
            className="p-1 hover:bg-blue-600 rounded"
            title={isMaximized ? "Restore" : "Maximize"}
          >
            <Maximize2 size={16} />
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-blue-600 rounded"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>
      
      {/* Content - only show when not minimized */}
      {!isMinimized && (
        <>
          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1 bg-gray-50">
            {Object.keys(groupedMessages).length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <MessageCircle size={48} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm font-medium">{t('chat.no_messages')}</p>
                  <p className="text-xs">{t('chat.be_first')}</p>
                </div>
              </div>
            ) : (
              Object.entries(groupedMessages).map(([dateString, dayMessages]) => (
                <div key={dateString}>
                  {/* Date header */}
                  <div className="flex justify-center my-4">
                    <div className="bg-gray-200 px-2 py-1 rounded-full">
                      <span className="text-xs font-medium text-gray-600">
                        {getDateHeaderText(dateString)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Messages for this date */}
                  {dayMessages.map((message) => {
                    const isCurrentUser = message.sender_id === currentUserId || 
                                        message.sender_name === sessionStorage.getItem('username');
                    
                    if (message.message_type === 'system_log' || 
                        message.message_type === 'event_creation' ||
                        message.message_type === 'event_update' ||
                        message.message_type === 'status_change' ||
                        message.message_type === 'mediator_response' ||
                        message.message_type === 'whatsapp_response') {
                      return (
                        <SystemMessage
                          key={message.id}
                          message={message}
                        />
                      );
                    }
                    
                    return (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onDeleteMessage={handleDeleteMessage}
                        currentUserRole={currentUserRole}
                      />
                    );
                  })}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Message input */}
          <MessageInput
            onSendMessage={handleSendMessage}
            disabled={!canSend}
            placeholder={canSend ? t('chat.type_message') : t('chat.revoked_event_notice')}
          />
        </>
      )}
    </div>
  );
};

export default ChatPopup;
